import React, { useState } from 'react'
import Select from 'react-select'
import styles from './AiReadinessForm.module.css'
import { useAiReadiness } from '../../context/AiReadinessContext.jsx'
import ProgressIndicator from './components/ProgressIndicator'

const designationOptions = [
  { value: "School Leadership", label: "School Leadership" },
  { value: "Principal", label: "Principal" },
  { value: "Vice Principal", label: "Vice Principal" },
  { value: "Administrator", label: "Administrator" },
  { value: "Teacher", label: "Teacher" },
  { value: "Student", label: "Student" },
  { value: "Other", label: "Other" }
]

export default function AiReadinessForm() {
  const { formData, updateFormData, submitForm } = useAiReadiness()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showErrors, setShowErrors] = useState(false)

  const getFieldErrors = () => {
    const errors = {};

    if (!formData.schoolName?.trim()) errors.schoolName = true;
    if (!formData.city?.trim()) errors.city = true;
    if (!formData.yourName?.trim()) errors.yourName = true;
    if (!formData.designation?.trim()) errors.designation = true;
    if (!formData.email?.trim()) errors.email = true;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = true;
    if (formData.mobileNumber?.length !== 10) errors.mobileNumber = true;

    return errors;
  }

  const allFieldErrors = getFieldErrors()
  const fieldErrors = showErrors ? allFieldErrors : {}

  const handleChange = (key, value) => {
    // Format mobile number - only allow digits and limit to 10 digits
    if (key === 'mobileNumber') {
      value = value?.replace(/[^0-9]/g, '')
      value = value?.slice(0, 10)
    }
    updateFormData(key, value)
  }

  const isFormValid = () => {
    return Object.keys(allFieldErrors).length === 0;
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!isFormValid()) {
      setShowErrors(true)
      return
    }

    setShowErrors(false)
    setIsLoading(true)
    setError(null)

    try {
      await submitForm()
      // Navigation happens automatically via jumpToOutcome in context
    } catch (err) {
      setError(err?.message || 'An error occurred while submitting the form')
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.formContainer}>
      <ProgressIndicator currentStep={7} totalSteps={7} />
      <h2 className={styles.formTitle}>Share your school details:</h2>

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* Row 1: School Name and City */}
        <div className={styles.formRow}>
          <div className={styles.formField}>
            <label className={`${styles.fieldLabel} ${fieldErrors.schoolName ? styles.error : ''}`}>School name*</label>
            <input
              type="text"
              placeholder="School name"
              value={formData.schoolName}
              onChange={(e) => handleChange('schoolName', e.target.value)}
              className={`${styles.input} ${fieldErrors.schoolName ? styles.error : ''}`}
            />
          </div>
          <div className={styles.formField}>
            <label className={`${styles.fieldLabel} ${fieldErrors.city ? styles.error : ''}`}>City*</label>
            <input
              type="text"
              placeholder="City"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              className={`${styles.input} ${fieldErrors.city ? styles.error : ''}`}
            />
          </div>
        </div>

        {/* Row 2: Your Name and Designation */}
        <div className={styles.formRow}>
          <div className={styles.formField}>
            <label className={`${styles.fieldLabel} ${fieldErrors.yourName ? styles.error : ''}`}>Your name*</label>
            <input
              type="text"
              placeholder="Full name"
              value={formData.yourName}
              onChange={(e) => handleChange('yourName', e.target.value)}
              className={`${styles.input} ${fieldErrors.yourName ? styles.error : ''}`}
            />
          </div>
          <div className={styles.formField}>
            <label className={`${styles.fieldLabel} ${fieldErrors.designation ? styles.error : ''}`}>Designation*</label>
            <Select
              options={designationOptions}
              value={designationOptions.find(opt => opt.value === formData.designation) || null}
              onChange={(option) => handleChange('designation', option ? option.value : '')}
              placeholder="Please select"
              isClearable
              menuPlacement="auto"
              className={styles.selectContainer}
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  border: `1px solid ${fieldErrors.designation ? '#d32f2f' : '#888888'}`,
                  borderRadius: '8px',
                  padding: '0px',
                  minHeight: '40px',
                  backgroundColor: '#ffffff',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  width: '100%'
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#5F4DC7' : state.isFocused ? '#f0f0f0' : '#ffffff',
                  color: state.isSelected ? '#ffffff' : '#262626',
                  padding: '8px 12px',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  cursor: 'pointer'
                }),
                singleValue: (base) => ({
                  ...base,
                  color: '#262626',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px'
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#a0a0a0',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px'
                }),
                menuList: (base) => ({
                  ...base,
                  maxHeight: '200px',
                  overflowY: 'auto'
                })
              }}
            />
          </div>
        </div>

        {/* Row 3: Email and Mobile Number */}
        <div className={styles.formRow}>
          <div className={styles.formField}>
            <label className={`${styles.fieldLabel} ${fieldErrors.email ? styles.error : ''}`}>Email*</label>
            <input
              type="email"
              placeholder="username@mail.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`${styles.input} ${fieldErrors.email ? styles.error : ''}`}
            />
          </div>
          <div className={styles.formField}>
            <label className={`${styles.fieldLabel} ${fieldErrors.mobileNumber ? styles.error : ''}`}>Mobile number*</label>
            <input
              type="tel"
              placeholder="+91 12345 67890"
              value={formData.mobileNumber}
              onChange={(e) => handleChange('mobileNumber', e.target.value)}
              className={`${styles.input} ${fieldErrors.mobileNumber ? styles.error : ''}`}
            />
          </div>
        </div>

        {/* Checkbox */}
        <div className={styles.checkboxGroup}>
          <input
            type="checkbox"
            checked={formData.getCall}
            onChange={(e) => handleChange('getCall', e.target.checked)}
            className={styles.checkbox}
          />
          <label className={styles.checkboxLabel}>Get a personalised readiness call</label>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{ color: '#d32f2f', marginTop: '10px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? 'Submitting...' : 'Get report'}
          </button>
        </div>
      </form>
    </div>
  )
}
